import {
  ChevronsUpDown,
  LogOutIcon,
  SettingsIcon,
  UserCircleIcon,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/ui/sidebar';

export interface SidebarUserProps {
  user: {
    name?: string;
    email?: string;
    image?: string;
  };
  links?: Array<{
    href: string;
    label: string;
    icon?: React.ReactNode;
  }>;
  logoutLabel?: string;
  onLogout?: () => void;
  onNavigate?: (path: string) => void;
}

export function SidebarUser({
  user,
  links = [],
  logoutLabel = 'Log out',
  onLogout,
  onNavigate,
}: SidebarUserProps) {
  const { isMobile } = useSidebar();

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <div className="flex size-8 items-center justify-center rounded-md bg-sidebar-primary text-sidebar-primary-foreground">
                {user.image ? (
                  <img
                    src={user.image}
                    alt={user.name || 'User'}
                    className="size-8 rounded-md object-cover"
                  />
                ) : (
                  <UserCircleIcon className="size-4" />
                )}
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-medium">
                  {user.name || 'User'}
                </span>
                <span className="truncate text-xs text-muted-foreground">
                  {user.email || ''}
                </span>
              </div>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-md"
            side={isMobile ? 'bottom' : 'right'}
            align="end"
            sideOffset={4}
          >
            <DropdownMenuLabel className="p-0 font-normal">
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <div className="flex size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  {user.image ? (
                    <img
                      src={user.image}
                      alt={user.name || 'User'}
                      className="size-8 rounded-lg object-cover"
                    />
                  ) : (
                    <UserCircleIcon className="size-4" />
                  )}
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">
                    {user.name || 'User'}
                  </span>
                  <span className="truncate text-xs">{user.email || ''}</span>
                </div>
              </div>
            </DropdownMenuLabel>
            {links.length > 0 ? (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuGroup>
                  {links.map((link) => (
                    <DropdownMenuItem
                      key={link.href}
                      onClick={() => {
                        if (onNavigate) {
                          onNavigate(link.href);
                          return;
                        }

                        window.location.assign(link.href);
                      }}
                    >
                      {link.icon || <SettingsIcon className="mr-2 size-4" />}
                      {link.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </>
            ) : null}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onLogout}>
              <LogOutIcon className="mr-2 size-4" />
              {logoutLabel}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
